import { useState, lazy, Suspense, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import SocScreens from './SocScreens';
import './Login.css';

const GlobeWidget = lazy(() => import('./GlobeWidget'));


const FEATURES = [
  { title: 'Centre de commandement', desc: 'Vue 360°, monitoring temps réel', icon: 'M12 2a10 10 0 100 20 10 10 0 000-20zM12 6v6l4 2' },
  { title: 'Gestion des agents', desc: 'Planning, présences, performances', icon: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7m-4 0a4 4 0 108 0 4 4 0 00-8 0M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75' },
  { title: 'Incidents & rondes', desc: 'Signalement, patrouilles, QR', icon: 'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01' },
  { title: 'Audit & déploiement', desc: 'Évaluation sites, plan de prévention', icon: 'M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11' },
  { title: 'Portail client', desc: 'Contrats, devis, facturation', icon: 'M2 3h20v14H2zM8 21h8M12 17v4' },
  { title: 'Comptabilité & RH', desc: 'Paie, charges, congés, recrutement', icon: 'M4 2v20h16V2H4zm4 4h8M8 10h8M8 14h5' },
  { title: 'Télésurveillance', desc: 'Alarmes, vidéo, journal de veille', icon: 'M23 7l-7 5 7 5V7zM1 5h15v14H1z' },
  { title: 'Intelligence IA', desc: 'Analyse contextuelle, recommandations', icon: 'M12 2a7 7 0 017 7c0 2.4-1.2 4.5-3 5.7V17H8v-2.3C6.2 13.5 5 11.4 5 9a7 7 0 017-7zM8 21h8M9 17v4M15 17v4' },
];

const SOC = ['VIDÉO', 'ALARMES', 'GPS', 'RADIO', 'RÉSEAU'];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);
  const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  // Charge le script Turnstile et rend le widget invisible (uniquement si une site key est configuree)
  useEffect(() => {
    if (!SITE_KEY) return undefined;
    const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    let interval;
    const renderWidget = () => {
      if (window.turnstile && turnstileRef.current && widgetIdRef.current === null) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          'error-callback': () => setTurnstileToken(''),
          'expired-callback': () => setTurnstileToken(''),
        });
      }
    };
    if (window.turnstile) {
      renderWidget();
    } else if (!document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
      const s = document.createElement('script');
      s.src = SCRIPT_SRC; s.async = true; s.defer = true;
      s.onload = renderWidget;
      document.head.appendChild(s);
    } else {
      interval = setInterval(() => { if (window.turnstile) { clearInterval(interval); renderWidget(); } }, 200);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [SITE_KEY]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password, turnstileToken);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Identifiants incorrects');
      // Token Turnstile a usage unique : reset pour permettre une nouvelle tentative
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
        setTurnstileToken('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lg">
      {/* ═══════ GAUCHE — CENTRE DE COMMANDEMENT ═══════ */}
      <div className="lg-left">
        {/* Particules */}
        <div className="lg-particles">
          {Array.from({ length: 15 }, (_, i) => (
            <span key={i} className="lg-particle" style={{
              left: `${5 + Math.random() * 90}%`,
              top: `${5 + Math.random() * 90}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${15 + Math.random() * 10}s`,
            }} />
          ))}
        </div>

        {/* Globe 3D */}
        <div className="lg-globe-glow" />
        <Suspense fallback={null}>
          <GlobeWidget />
        </Suspense>

        {/* Radar overlay */}
        <div className="lg-radar">
          <div className="lg-radar-c lg-radar-c1" />
          <div className="lg-radar-c lg-radar-c2" />
          <div className="lg-radar-c lg-radar-c3" />
          <div className="lg-radar-c lg-radar-c4" />
          <div className="lg-radar-sweep" />
          <div className="lg-radar-center" />
        </div>

        {/* Logo haut gauche */}
        <div className="lg-brand">
          <span className="lg-brand-logo gss-marque-produit">GSS-360</span>
        </div>

        {/* Hero */}
        <div className="lg-hero">
          <h1 className="lg-title-big">
            <span className="lg-gss">GSS</span>
            <span className="lg-360">-360</span>
          </h1>

          <div className="lg-badge">
            <span className="lg-badge-dot" />
            Plateforme de commandement s&eacute;curitaire
          </div>

          <p className="lg-hero-desc">
            <span className="lg-w">Surveillance.</span>{' '}
            <span className="lg-w">Coordination.</span>{' '}
            <span className="lg-w lg-w-red">Intervention.</span>{' '}
            <span className="lg-w">Intelligence.</span>
            <br />
            <span className="lg-hero-desc2">Une plateforme centralis&eacute;e con&ccedil;ue pour les entreprises de s&eacute;curit&eacute; modernes.</span>
          </p>

          <div className="lg-features">
            {FEATURES.map((f, i) => (
              <div className="lg-fcard" key={i}>
                <svg className="lg-fcard-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d={f.icon} />
                </svg>
                <div className="lg-fcard-title">{f.title}</div>
                <div className="lg-fcard-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* SOC screens */}
        <SocScreens />

        {/* Barre SOC */}
        <div className="lg-soc">
          {SOC.map((s, i) => (
            <div className="lg-soc-item" key={i}>
              <span className="lg-soc-label">{s}</span>
              <span className="lg-soc-dot" style={{ animationDelay: `${i * 0.3}s` }} />
            </div>
          ))}
        </div>
      </div>

      {/* ═══════ SÉPARATEUR PREMIUM ═══════ */}
      <div className="lg-sep" />

      {/* ═══════ DROITE — FORMULAIRE ═══════ */}
      <div className="lg-right">
        <div className="lg-form-block">
          {/* Badge système */}
          <div className="lg-system-badge">
            <span className="lg-system-status">
              <span className="lg-system-dot" />
              SYST&Egrave;ME EN LIGNE
            </span>
          </div>

          {/* Logo G */}
          <div className="lg-form-logo-wrap">
            <svg className="lg-form-g" viewBox="0 0 56 56" fill="none">
              <rect width="56" height="56" rx="14" fill="#FFFFFF" stroke="#8B1A1A" strokeWidth="2" />
              <text x="50%" y="54%" dominantBaseline="middle" textAnchor="middle"
                fill="#C00000" fontFamily="Orbitron, monospace" fontSize="30" fontWeight="800">G</text>
            </svg>
            <div className="lg-form-logo-line" />
          </div>

          <h2 className="lg-form-title">Connexion</h2>
          <p className="lg-form-subtitle">Acc&eacute;dez &agrave; votre espace s&eacute;curis&eacute;</p>

          {error && <div className="lg-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <label className="lg-label" htmlFor="lg-email">E-mail</label>
            <div className="lg-input-wrap">
              <svg className="lg-icon" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              <input id="lg-email" type="email" value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@gss-360.com" required autoFocus />
            </div>

            <div className="lg-label-row">
              <label className="lg-label" htmlFor="lg-pw">Mot de passe</label>
              <span className="lg-forgot">Mot de passe oubli&eacute; ? Contactez l'administrateur.</span>
            </div>
            <div className="lg-input-wrap">
              <svg className="lg-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <input id="lg-pw" type={showPassword ? 'text' : 'password'}
                value={password} onChange={e => setPassword(e.target.value)}
                placeholder="••••••••••" required />
              <button type="button" className="lg-eye" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                {showPassword ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                )}
              </button>
            </div>

            <div className="lg-remember">
              <input type="checkbox" id="lg-remember" checked={remember} onChange={e => setRemember(e.target.checked)} />
              <label htmlFor="lg-remember">Se souvenir de moi</label>
            </div>

            {/* Widget Cloudflare Turnstile (invisible) — rendu uniquement si une site key est configuree */}
            {SITE_KEY && <div ref={turnstileRef} className="cf-turnstile" style={{ marginBottom: 16 }} />}

            <button type="submit" className="lg-btn" disabled={loading}>
              {loading ? <span className="lg-spinner" /> : (
                <>
                  ACC&Eacute;DER &Agrave; GSS-360
                  <svg className="lg-btn-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* Certifications */}
          <div className="lg-certs">
            <span className="lg-certs-title">S&eacute;curis&eacute; et certifi&eacute;</span>
            <div className="lg-certs-row">
              <span className="lg-cert">CONFORME ISO 27001</span>
              <span className="lg-cert">RGPD</span>
              <span className="lg-cert">SSL 256-BIT</span>
            </div>
          </div>

          <div className="lg-form-footer">
            <span className="lg-copy">&copy; 2026 Gladiator Security Systems — Éditeur de GSS-360, intégrateur de systèmes de sécurité</span>
            <span className="lg-copy">RCCM TG-LFW-01-2024-B13-00449 | NIF 1001930788</span>
          </div>
        </div>
      </div>
    </div>
  );
}
